/**
 * What the app has told you (D-324).
 *
 * The model has existed since D-285 and nothing read it: four dispatch sites
 * write rows and production has been accumulating them unreadably.
 */
export type NotificationRow = {
  id: string

  /**
   * Which template produced it.
   *
   * **Where a row goes depends on this, not on who is reading.** Both halves
   * of a private-lesson conversation carry the same subject, and sixteen
   * people here are staff and a parent on one account (D-305) - so "is she
   * staff" answers the wrong question, and answered it wrongly for exactly
   * those sixteen.
   */
  templateKey: string

  title: string
  body: string | null

  /**
   * What it is about — `"Ask"`, `"Dancer"`. Null for most templates.
   *
   * A string rather than a union, deliberately: D-285's claim is that a new kind
   * of notification is a template row plus a subject type and never a change to
   * the panel. A union here would make every new kind a front-end change too.
   */
  subjectType: string | null
  subjectId: string | null

  sentAt: string
  isRead: boolean
}

export type NotificationList = {
  rows: NotificationRow[]

  /** Unread, never outstanding — whether a thing still needs doing is the subject's business. */
  unread: number
}

export async function fetchNotifications(): Promise<NotificationList> {
  const res = await fetch('/api/notifications')
  if (!res.ok) throw new Error(`Could not load your notifications (${res.status})`)
  return res.json()
}

export async function markRead(id: string): Promise<void> {
  const res = await fetch(`/api/notifications/${id}/read`, { method: 'POST' })
  if (!res.ok) throw new Error(`Could not mark that as read (${res.status})`)
}

export async function markAllRead(): Promise<void> {
  const res = await fetch('/api/notifications/read', { method: 'POST' })
  if (!res.ok) throw new Error(`Could not mark those as read (${res.status})`)
}

/**
 * Takes everything off the list (D-391).
 *
 * Not the same as marking read, which is what existed and what was reported as
 * insufficient: read answers "have I seen this", and the bell still held every
 * row afterwards. Soft on the server — the rows stay and stop being served, so
 * what the studio told a family is not something a family can erase.
 */
export async function clearAll(): Promise<void> {
  const res = await fetch('/api/notifications/clear', { method: 'POST' })
  if (!res.ok) throw new Error(`Could not clear those (${res.status})`)
}

/**
 * Where a notification takes you when you open it.
 *
 * **Per subject type, and unknown means nowhere** — a row whose subject this
 * version does not recognise still renders and simply does not navigate, which
 * is what keeps D-285's promise true: a new kind is a template row and a subject
 * type, and an app that had not shipped yet must not break on one.
 */
export function hrefForSubject(
  row: NotificationRow,
  reader: { canViewPeople: boolean; isParent?: boolean; teaches?: boolean },
): string | null {
  if (!row.subjectId) return null

  /*
   * <b>Where to pay is decided by what the row says, not by what it points
   * at</b> (D-756, amended).
   *
   * D-756 fixed the *writer*: `PaymentKind` names the booking and the envelope
   * now believes it, so a new row opens her children's week where
   * `PaymentLine` draws the teacher's link (D-691). It did nothing for the
   * rows **already written**, and that is most of them - the subject is stored
   * on the notification when it is sent, so every payment row in the database
   * still carries `Ask` and still landed on Home. `asked`, after the fix
   * reached QA: *"Clicking on it still brings you to your homepage."*
   *
   * <b>A migration would have been the wrong tool.</b> These rows are a log of
   * what was said; rewriting old ones to make a link work is editing history
   * to patch a reader. The reader is the thing that was wrong, and it has
   * everything it needs - the template key names what the message <i>is</i>,
   * which is the same thing D-437 already reads a booking's side from.
   *
   * Ahead of the switch, because it must answer the same way whichever subject
   * the row happens to carry - that is the whole point of it.
   */
  /*
   * `payment.reminder` too, and leaving it out was a real bug (D-866). The
   * reminder's whole purpose is *here is how to pay*, its in-app body carries no
   * link, and without this it fell past to `case 'Ask'` and opened **Home** -
   * which is the owner's own complaint, verbatim: *"when you click on the paying
   * bailey jo for lesson it just brings you to the home page and doesn't show you
   * how to pay."*
   *
   * The envelope rewrites a money message's subject to the ask (`SendAsync`), so
   * a row cannot be told apart by its subject - which is exactly why D-756 keyed
   * this on the template instead. A new key has to be added here or it inherits
   * the bug the key-matching was introduced to fix.
   */
  if (row.templateKey?.startsWith('payment.due') || row.templateKey?.startsWith('payment.reminder')) {
    return '/schedule/dancers'
  }

  switch (row.subjectType) {
    /*
     * A cover request, which she answers from home (D-365).
     *
     * **It pointed at `/schedule`** — right while `CoverPanel` sat under her
     * week, and wrong the moment cover moved. The bell is how a colleague
     * learns she has been asked at all (D-328), so this is the feature's main
     * entry path: tapping the notification would have landed her on a screen
     * with nothing about cover on it. A link that refuses the person holding
     * it, and the fourth time this shape has appeared in three commits — every
     * one of them found by following the link rather than by reading the
     * component.
     */
    case 'Cover':
      return '/'

    /*
     * A makeup one level up (D-403).
     *
     * **Both audiences go home, and that is not laziness.** The teacher answers
     * it from the requests panel on hers; the family reads the answer beside
     * the schedule and the offer on hers. Neither has a screen of its own, and
     * inventing one would be a destination nobody asked for.
     *
     * The subject id is the attendance row rather than a request entity —
     * there is no request entity, deliberately (D-403) — so there is nothing to
     * deep-link to even if there were somewhere to link.
     */
    case 'MakeupRequest':
      return '/'

    /*
     * The dancer record, which is where standing lives (D-303) - and it is
     * gated on `ViewPeople`, so a parent reading the same alert about her own
     * child gets her schedule instead. Better no link than a link that says no.
     */
    /*
     * **The parent branch names the child and the view** (D-391).
     *
     * It returned a bare `/schedule` — the redirect D-388 banned four cases
     * below for being resolved by preference rather than by name, so a guardian
     * who also teaches landed on her teaching week. And it **threw away the
     * dancer id it was holding**: this notification is about one specific child
     * and opened the alphabetically-first one's schedule.
     *
     * D-344 fixed that shape for private requests and excused itself here on
     * the grounds that a subject id could not build `?dancer=`. That excuse
     * does not apply to this case, where the subject *is* the dancer, and
     * nobody re-checked it — a parent with three dancers is told one of them is
     * below the threshold and shown the wrong child.
     */
    case 'Dancer':
      return reader.canViewPeople
        ? `/admin/dancers/${row.subjectId}`
        : `/schedule/dancers?dancer=${row.subjectId}`

    /*
     * A class of hers was called off, or somebody else is taking it (D-356).
     *
     * **Her schedule, not the class.** `subjectId` is a `StudioClassId`, and no
     * screen in the app takes one — the parent's week is where the change shows
     * up, and the message already names the date. Without this case the bell
     * rendered a row that navigated nowhere, which is a notification that
     * cannot be acted on.
     */
    /*
     * **Her children's week, named explicitly** (D-388).
     *
     * `/schedule` is a redirect, and `ScheduleScreen` resolves it by preferring
     * `teaches` — so a guardian who also teaches was sent to her *teaching*
     * week, which cannot contain her child's class. These notifications go to
     * guardians (`FamiliesInClassAsync`), so the destination is the children's
     * view, and a teacher reading one about her own child wants that too.
     *
     * The bug only bites the sixteen dual-role accounts, which is exactly why
     * nothing caught it: for everyone else the redirect happens to land right.
     */
    case 'Class':
      return '/schedule/dancers'

    /*
     * **A held room, so the week it is in** (D-391, closing a gap D-388 opened).
     *
     * D-388 added `NotificationSubject.Booking` and no case here, so every
     * `booking.approved` marked itself read and navigated nowhere — the exact
     * *"row that navigates nowhere"* the `Class` case above was written to
     * prevent, reintroduced one entry later. Nothing failed, because the subject
     * type and the reader are in different languages and nothing bound them.
     *
     * ***Keyed on the template, not on the reader, and that took a second
     * try.*** The first fix asked `canViewPeople` — but **a teacher holds
     * `ViewPeople`**, so a dual-role parent receiving the *family* copy about
     * her own child was sent to her teaching week, which cannot contain it.
     * That is D-388's own bug, one entry later, and it is why the confirmation
     * is now two template keys: one message going to two kinds of person cannot
     * say which screen it belongs on, and the reader is documented to switch on
     * the key rather than to guess from capabilities.
     *
     * The week rather than the booking, because a booking has no screen of its
     * own and the message already names the day.
     */
    /*
     * **The suffix, not one exact key** (D-437). The exact match was right while
     * `booking.approved.family` was the only family-facing booking key — and it
     * meant the next one silently inherited the very bug the two-key split was
     * made to fix: `booking.cancelled.family` fell to the `else` and sent a
     * parent to her teaching week, which cannot contain it.
     *
     * Every booking key names its side, which is what that split decided. Read
     * the side rather than enumerate the keys, and a fourth one routes itself.
     */
    case 'Booking':
      return row.templateKey.endsWith('.family')
        ? '/schedule/dancers'
        : '/schedule/classes'

    /*
     * **A session of a class, so the week it is in** — paying for a solo, duet
     * or trio once its register is taken (`PILOT-FEEDBACK` item A).
     *
     * Keyed on the side the template addresses, exactly as `Booking` above, and
     * for D-437's reason rather than by copying it: every key naming its side is
     * what lets a fourth one route itself. All three of the
     * `payment.due.class.*` keys are family-facing, so today this only ever
     * takes the first arm — the second is there because the teacher's copy is
     * the obvious next key, and leaving it to fall through to `else` is exactly
     * how `booking.cancelled.family` sent a parent to her teaching week.
     *
     * The week rather than the session, because a class occurrence has no screen
     * of its own and the message already names the day.
     */
    case 'Occurrence':
      return row.templateKey.endsWith('.family')
        ? '/schedule/dancers'
        : '/schedule/classes'

    /*
     * A request on the envelope (D-543). Keyed on the side the template
     * addresses, as `Booking` is above (red team O8): a family's rows live on
     * Home, because a parent has no Requests screen (gate Q2), and sending her
     * to `/asks` would be a link that refuses the person holding it.
     */
    /*
     * A request on the envelope (D-543), which is every private-lesson
     * message too since D-553.
     *
     * A family's copy lands on Home, where the row is (gate Q2); anybody who
     * teaches or runs the studio lands on Requests, which lists her family's
     * rows too when she has both parts. That last clause is D-305's lesson:
     * keyed on staff-or-not, the sixteen accounts that are staff and a parent
     * at once were sent to a teacher screen that could not contain their own
     * request. And Home rather than a bare `/schedule` is D-344's: that
     * opened the first child alphabetically, so a message about Ellie opened
     * Becky. The row on Home is household-scoped and needs no dancer id.
     */
    case 'Ask':
      return row.templateKey.endsWith('.family') || (reader.isParent && !reader.teaches && !reader.canViewPeople)
        ? '/'
        : '/asks'

    /*
     * Somebody said something (D-623). The subject is the conversation rather
     * than the message - one unread row per conversation per reader is what
     * makes the suppression rule a query - so the row opens the conversation,
     * and the same one for every kind of reader.
     */
    case 'Conversation':
      return `/messages/${row.subjectId}`

    default:
      return null
  }
}

/**
 * One notification, for the deep link a push carries (D-530).
 *
 * **Not read out of the panel.** The list returns the newest thirty and a
 * tapped push can be older — a banner left on a lock screen overnight is
 * exactly the row somebody is trying to open.
 */
export async function fetchNotification(id: string): Promise<NotificationRow> {
  const res = await fetch(`/api/notifications/${id}`)
  if (!res.ok) throw new Error(`Could not open that notification (${res.status})`)
  return res.json()
}
